import {
  ApiListInfiniteScrollResponse,
  ApiListResponse,
  GetListApiConfig,
  isBillingPlatformApiErrorResponse,
  Maybe,
  SortOrder,
} from '@monetize/types/common';
import { ENVIRONMENT } from '@monetize/ui/config';
import { isPlainObject, nullifyEmptyStrings } from '@monetize/utils/core';
import axios, {
  AxiosError,
  AxiosProgressEvent,
  AxiosRequestHeaders,
  InternalAxiosRequestConfig,
} from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import isString from 'lodash/isString';
import startCase from 'lodash/startCase';
import { getAppStore } from '../store/appStore';
import { ROUTES } from '../constants';
import { logger } from '../services/logger';
import Sentry from '../services/sentry';
import { AddToastItem } from '../services/toast';
import {
  accessTokenState,
  activeToastState,
  currentTenantState,
  currentUserIdState,
  isLoggingOutState,
  navigationState,
  refreshAccessTokenState,
  tempRedirectInfoState,
} from '../store/global.store';
import { composeGetQuery } from './utils';

/**
 * Only logout for 401s on routes that start with these prefixes
 * Connector services or other 3rd party api requests may return 401s and that does not mean it is an authentication failure for MN
 *
 * /api/share/ is excluded, but all other /api/ routes are included - never send anonymous user to login page
 */
const LOGOUT_ON_401_ROUTE_PREFIXES = [
  /^\/api\/(?!share\/).*/,
  /^\/templates(\/|$)/,
];
/**
 * If the API request is one of these paths, remove the baseURL
 * These are set when running BE locally or when LOCAL_RULES environment variable is set to true
 */
const LOCAL_RULES_PATHS = [
  '/rules-v3',
  '/experience',
  '/api/pdf',
  '/templates',
];

const LOCAL_LAMBDAS_PATHS = ['/oauth', '/slack'];

const axiosInstance = axios.create({
  baseURL: ENVIRONMENT.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Attempt to auto-refresh the access token if we get a 401
 * If a user focuses on the page, react-query will make requests
 * before a refresh attempt is able to be made. This handles the refresh
 * just-in-time and ensures the updated access token if saved to the store.
 *
 * {@link https://github.com/Flyrell/axios-auth-refresh}
 */
const refreshAuthLogic = async (failedRequest: any) => {
  try {
    const refreshAccessToken = getAppStore().get(refreshAccessTokenState);
    if (!refreshAccessToken) {
      throw failedRequest;
    }

    const navigation = getAppStore().get(navigationState);
    const isLoggingOut = getAppStore().get(isLoggingOutState);
    if (navigation?.route === ROUTES.AUTH_LOGOUT || isLoggingOut) {
      logger.log('[HTTP][REQ][UNAUTHORIZED] Skipping refresh during logout');
      throw failedRequest;
    }

    logger.log(
      `[HTTP][REQ][UNAUTHORIZED] Attempting auto-refresh`,
      failedRequest.response.config.url,
    );

    const accessToken = await refreshAccessToken();
    // request will be retired with this header
    failedRequest.response.config.headers.authorization = `Bearer ${accessToken}`;
    // Update store so all other paused request have the up-to-date data
    getAppStore().set(accessTokenState, accessToken);
  } catch (ex) {
    throw failedRequest;
  }
};

createAuthRefreshInterceptor(axiosInstance, refreshAuthLogic);

// Add a request interceptor
axiosInstance.interceptors.request.use(
  (confA: InternalAxiosRequestConfig) => {
    const config = confA as MAxiosRequestConfig;

    // If running rules locally, remove baseURL - proxy configuration will handle it
    if (
      ENVIRONMENT.LOCAL_RULES &&
      /**
       * If the API request is one of these paths, remove the baseURL
       */
      LOCAL_RULES_PATHS.some((prefix) => config.url?.startsWith(prefix))
    ) {
      config.baseURL = undefined;
    }

    // If running lambdas locally, remove baseURL - proxy configuration will handle it
    if (
      ENVIRONMENT.LOCAL_LAMBDAS &&
      LOCAL_LAMBDAS_PATHS.some((prefix) => config.url?.startsWith(prefix))
    ) {
      config.baseURL = undefined;
    }

    const userId = getAppStore().get(currentUserIdState);
    const currentTenant = getAppStore().get(currentTenantState);
    const accessToken = getAppStore().get(accessTokenState);

    config.metadata = { startTime: new Date() };

    config.headers = config.headers || ({} as AxiosRequestHeaders);

    if (
      config.method !== 'get' &&
      !config.data &&
      !config.axiosConfig?.keepEmptyPayload
    ) {
      config.data = config.data ?? undefined;
    }

    if (
      config.method !== 'get' &&
      !config.axiosConfig?.skipNullifyStrings &&
      config.data &&
      typeof config.data === 'object' &&
      (isPlainObject(config.data) || Array.isArray(config.data))
    ) {
      try {
        config.data = nullifyEmptyStrings(config.data);
      } catch (ex) {
        logger.warn('[HTTP][REQ][ERROR] Failed to nullify empty strings', ex);
      }
    }

    if (!config.axiosConfig?.excludeTokenIdFromHeader) {
      config.headers.authorization = `Bearer ${accessToken}`;
    }

    if (!config.axiosConfig?.excludeTenantIdFromHeader && currentTenant?.id) {
      config.headers['x-tenant-id'] = currentTenant.id;
    }

    if (!config.axiosConfig?.excludeUserIdFromHeader && userId) {
      config.headers['x-user-id'] = userId;
    }

    if (config.axiosConfig?.customXTenantId) {
      config.headers['x-tenant-id'] = config.axiosConfig.customXTenantId;
    }

    logger.info(`[HTTP][REQ][${config?.method?.toUpperCase()}]`, config.url, {
      data: config.data,
      params: config.params,
      headers: config.headers?.toJSON(),
    });

    return config as InternalAxiosRequestConfig;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => {
    logger.info(
      `[HTTP][RES][${response.config?.method?.toUpperCase()}]`,
      response.config.url,
      { data: response.data },
    );
    return response;
  },
  async (error: AxiosError) => {
    const startTime = (error?.config as MAxiosRequestConfig)?.metadata
      ?.startTime;
    const duration = startTime
      ? new Date().getTime() - startTime.getTime()
      : -1;

    const currentTenant = getAppStore().get(currentTenantState);
    const currentUserId = getAppStore().get(currentUserIdState);
    const url = error.response?.config?.url;
    const statusCode = error?.response?.status || 400;
    const method = error?.request?.method;
    const config = error?.config as MAxiosRequestConfig;

    logger.warn(
      '[HTTP][RES][ERROR]',
      error.name,
      error.message,
      error.response?.data,
    );

    // Handle retry on 409 conflict
    if (
      statusCode === 409 &&
      config?.axiosConfig?.retryOnConflict &&
      !(config as any)._retry
    ) {
      logger.info(
        `[HTTP][RES][CONFLICT] Retrying request due to 409 conflict`,
        config.url,
      );

      // Mark this request as a retry to prevent infinite loop
      (config as any)._retry = true;

      // Retry the request
      return axiosInstance.request(config);
    }

    if (
      statusCode === 401 &&
      LOGOUT_ON_401_ROUTE_PREFIXES.some((prefix) => prefix.test(url || ''))
    ) {
      getAppStore().set(tempRedirectInfoState, {
        tenantId: currentTenant?.id,
        redirectUrl: `${window.location.pathname}${window.location.search}`,
      });
      getAppStore().set(navigationState, { route: ROUTES.AUTH_LOGOUT });
    }

    // Log errors to Sentry
    if (
      statusCode >= 500 &&
      !axios.isCancel(error) &&
      !ENVIRONMENT.BASE_URL?.includes('localhost')
    ) {
      Sentry.captureException(error, {
        tags: {
          type: 'API_CALL',
          apiUrl: url,
          apiMethod: method,
          duration,
          statusCode,
          tenantId: currentTenant?.id,
          userId: currentUserId,
        },
      });
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;

export interface MAxiosCustomConfig {
  excludeTenantIdFromHeader?: boolean;
  excludeUserIdFromHeader?: boolean;
  excludeTokenIdFromHeader?: boolean;
  customXTenantId?: string;
  skipNullifyStrings?: boolean;
  keepEmptyPayload?: boolean;
  retryOnConflict?: boolean;
}

export interface MAxiosRequestConfig extends Partial<InternalAxiosRequestConfig> {
  axiosConfig?: MAxiosCustomConfig;
  metadata?: { startTime?: Date };
}

export async function apiGet<T = any>(
  resource: string,
  config?: MAxiosRequestConfig,
) {
  return axiosInstance.get<T>(resource, config);
}

function calculateProgress({ totalPages, pageable }: ApiListResponse) {
  if (!pageable) {
    return 0;
  }
  const currentPage = pageable.pageNumber + 1; // Convert 0-based index to 1-based
  const progress = (currentPage / totalPages) * 100;
  return progress;
}

/**
 * Auto-paginate all results for APIs
 */
export async function apiGetAllList<T = unknown>(
  resource: string,
  {
    rows = 100,
    sortField,
    sortOrder,
    filters,
    config,
    signal,
    onProgress,
  }: {
    rows?: number;
    filters?: any;
    sortField?: string;
    sortOrder?: SortOrder;
    config?: GetListApiConfig | MAxiosRequestConfig;
    signal?: AbortSignal;
    onProgress?: (progress: number) => void;
  } = {},
): Promise<T[]> {
  let done = false;
  let results: T[] = [];
  let page = 0;
  // ensure sort information included on config is not overwritten
  sortField = sortField || (config as GetListApiConfig)?.sortField;
  sortOrder = sortOrder || (config as GetListApiConfig)?.sortOrder;
  onProgress && onProgress(0);
  while (!done && !signal?.aborted) {
    const params = composeGetQuery(
      { ...config, page, rows, sortField, sortOrder },
      filters,
    );
    const { data } = await axiosInstance.get<ApiListResponse<T>>(resource, {
      params,
      signal,
    });
    results = [...results, ...data.content];
    done = page + 1 >= data.totalPages;
    page++;
    onProgress && onProgress(calculateProgress(data));
  }
  return results;
}

/**
 * Auto-paginate for APIs that use nextPageToken to fetch next set of records
 */
export async function apiGetAllListInfiniteScroll<T = unknown>(
  resource: string,
  {
    rows = 100,
    sortField,
    sortOrder,
    filters,
    config,
  }: {
    rows?: number;
    filters?: any;
    sortField?: string;
    sortOrder?: SortOrder;
    config?: MAxiosRequestConfig;
  } = {},
): Promise<T[]> {
  let done = false;
  let results: T[] = [];
  let nextPageToken: Maybe<string> = undefined;
  while (!done) {
    const params = composeGetQuery(
      { ...config, nextPageToken, rows, sortField, sortOrder },
      filters,
    );
    const { data } = await axiosInstance.get<ApiListInfiniteScrollResponse<T>>(
      resource,
      {
        params,
      },
    );
    results = [...results, ...data.content];
    done = !data.pageable?.nextPageToken;
    nextPageToken = data.pageable?.nextPageToken;
  }
  return results;
}

export async function apiPost<T = any>(
  resource: string,
  data?: any,
  config?: MAxiosRequestConfig,
) {
  return axiosInstance.post<T>(resource, data, {
    ...config,
    headers: {
      ...config?.headers,
    },
  });
}

export async function apiDelete<T = any>(
  resource: string,
  config?: MAxiosRequestConfig,
) {
  return axiosInstance.delete<T>(resource, config);
}

export async function apiPatch<T = any>(
  resource: string,
  data?: any,
  config?: MAxiosRequestConfig,
) {
  return axiosInstance.patch<T>(resource, data, config);
}

export async function apiPut<T = any>(
  resource: string,
  data?: any,
  config?: MAxiosRequestConfig,
) {
  return axiosInstance.put<T>(resource, data, config);
}

export async function apiUpload<T = any>(
  resource: string,
  data?: any,
  config?: MAxiosRequestConfig,
  progressCallback?: (progress: number) => void,
  usePut?: boolean,
) {
  return axiosInstance[usePut ? 'put' : 'post']<T>(resource, data, {
    ...config,
    headers: {
      ...config?.headers,
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      const uploadPercentage = parseInt(
        String(
          Math.round(
            ((progressEvent.loaded / (progressEvent.total ?? 1)) *
              100) as number,
          ),
        ),
        10,
      );

      progressCallback && progressCallback(uploadPercentage);
    },
  });
}

export async function apiUploadPut<T = any>(
  resource: string,
  data?: any,
  config?: MAxiosRequestConfig,
  progressCallback?: (progress: number) => void,
) {
  return axiosInstance.put<T>(resource, data, {
    ...config,
    headers: {
      ...config?.headers,
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      const uploadPercentage = parseInt(
        String(
          Math.round(
            ((progressEvent.loaded / (progressEvent.total ?? 1)) *
              100) as number,
          ),
        ),
        10,
      );

      progressCallback && progressCallback(uploadPercentage);
    },
  });
}

export const handleApiErrorToast = (err: any, defaultValues?: AddToastItem) => {
  try {
    logger.info('[API ERROR]', { err });
    if (axios.isCancel(err) || err.name === 'AbortError') {
      // xhr's Promise was cancelled, probably by our code with AbortController.abort()
      return;
    }

    const { summary, detail } = parseMessageFromError(err, defaultValues);

    if (summary) {
      getAppStore().set(activeToastState, {
        life: 5000,
        severity: 'error',
        summary,
        detail,
      });
    }
  } catch (error) {
    logger.warn('[handleApiErrorToast]', error);
  }
};

const MAX_FIELD_ERRORS_SHOWN = 3;

// Labels lodash's startCase would get wrong ("Cc Emails").
const FIELD_ERROR_LABEL_OVERRIDES: Record<string, string> = {
  ccEmails: 'CC Emails',
  bccEmails: 'BCC Emails',
};

/**
 * Labels a field error by the last property in its JSON path, e.g.
 * `$.addressData.billingAddressId` -> "Billing Address Id". The root path `$`
 * has no label.
 */
const getFieldErrorLabel = (path: string) => {
  const property = path
    .replace(/\[\d+\]/g, '')
    .split('.')
    .pop();
  if (!property || property === '$') {
    return '';
  }
  return FIELD_ERROR_LABEL_OVERRIDES[property] ?? startCase(property);
};

/**
 * Formats the field-level validation errors in `context.errors` (each with a
 * JSON `path` and a `message`) as one line per error, capped so a long list
 * doesn't flood the toast. Returns null when there are none.
 */
const getFieldErrorsDetail = (errorData: any): string | null => {
  const errors: unknown = errorData?.context?.errors;
  if (!Array.isArray(errors)) {
    return null;
  }
  const lines = errors
    .filter(
      (error) =>
        isPlainObject(error) &&
        isString(error.path) &&
        isString(error.message) &&
        error.message,
    )
    .map(({ path, message }) => {
      const label = getFieldErrorLabel(path);
      return label ? `${label}: ${message}` : message;
    });
  if (lines.length === 0) {
    return null;
  }
  const hiddenCount = lines.length - MAX_FIELD_ERRORS_SHOWN;
  return [
    ...lines.slice(0, MAX_FIELD_ERRORS_SHOWN),
    ...(hiddenCount > 0 ? [`and ${hiddenCount} more`] : []),
  ].join('\n');
};

export const parseMessageFromError = (
  err: any,
  defaultValues?: AddToastItem,
) => {
  let summary = defaultValues?.summary || '';
  let detail = defaultValues?.detail || '';
  let errorData = err?.response?.data;

  // Handle ArrayBuffer error responses (from requests with responseType: 'arraybuffer')
  if (errorData instanceof ArrayBuffer) {
    try {
      const textDecoder = new TextDecoder();
      const jsonString = textDecoder.decode(errorData);
      errorData = JSON.parse(jsonString);
    } catch (parseError) {
      errorData = null;
    }
  }

  const fieldErrorsDetail = getFieldErrorsDetail(errorData);

  if (errorData && errorData.apierrorresponse) {
    const errorResp = errorData.apierrorresponse;
    if (errorResp.status === 'BAD_REQUEST') {
      summary = errorResp.message;

      if (errorResp.subErrors?.length > 0) {
        // detail = `${errorResp.subErrors[0].field} ${errorResp.subErrors[0].message}`;
        detail = `${errorResp.subErrors[0].message}`;
      }
    } else {
      // BREN-TODO need more implementation of error handlers
    }
  } else if (
    isBillingPlatformApiErrorResponse(errorData) &&
    isString(errorData.error?.details) &&
    errorData.error.details
  ) {
    summary = errorData.message || err.name || 'Error';
    detail = getFieldErrorsDetail(errorData.error) ?? errorData.error.details;
  } else if (fieldErrorsDetail) {
    summary = errorData.details || 'Error';
    detail = fieldErrorsDetail;
  } else if (errorData && errorData.details) {
    if (err?.response?.status === 404) {
      summary = 'Not Found';
    } else {
      summary = err.name || 'Error';
    }
    detail = errorData.details;
  } else if (errorData && isString(errorData.message) && errorData.message) {
    // general handlers without correct error api response
    summary = err.name || 'Error';
    detail = errorData.message;
  } else if (err?.response?.data?.failedEvents) {
    // usage error handler
    summary = err.response.data?.status;
    detail = err.response.data?.failedEvents[0].reason;
  } else if (
    err instanceof Error &&
    !err.message?.startsWith('Request failed with status code')
  ) {
    summary = 'Error';
    detail = err.message;
  } else {
    // general handlers without correct error api response
    summary = err.name || 'Error';
    detail = !err.message?.startsWith('Request failed with status code')
      ? err.message
      : 'Something went wrong. Please try again later.';
  }
  if (summary === 'AxiosError') {
    summary = 'Error';
  }
  return { summary, detail };
};

export function isAxiosError(error: any): error is AxiosError {
  return !!error?.response;
}

export function getMessageFromError(
  error: unknown,
  fallback = 'Something went wrong. Please try again later.',
): string {
  if (!isAxiosError(error)) {
    return fallback;
  }

  if (isString((error.response?.data as any)?.details)) {
    return (error.response?.data as any)?.details || fallback;
  }

  if (isString((error.response?.data as any)?.message)) {
    return (error.response?.data as any)?.message || fallback;
  }

  return fallback;
}

/**
 * Checks if the given error is an Axios error with any of the specified status codes.
 *
 * @param {any} error - The error object to check.
 * @param {...number[]} statusCodes - The status codes to check against.
 * @returns {boolean} True if the error is an Axios error with any of the specified status codes, false otherwise.
 */
export const isAxiosErrorMatchedStatus = (
  error: any,
  ...statusCodes: number[]
) => {
  return (
    isAxiosError(error) && statusCodes.includes(error.response?.status || 0)
  );
};
